import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { InputTextModule } from 'primeng/inputtext';
import { DateRangePickerComponent, DateRange } from '../../components/date-range-picker/date-range-picker.component';
import { KpiCardComponent } from '../../components/kpi-card/kpi-card.component';
import { RevenueChartComponent, RevenueChartData } from '../../components/revenue-chart/revenue-chart.component';
import { TicketMetricsByEventComponent } from '../../components/ticket-metrics-by-event/ticket-metrics-by-event.component';
import { TicketMetricsData } from '../../models/ticket-metrics-data.model';
import { KpiCardData } from '../../models/kpi-card-data.model';
import { mapApiResponseToKpiCard } from '../../utils/kpi-formatter.util';
import { getPayoutStatusSubText, parsePayoutStatus } from '../../utils/payout-status.util';
import { RevenueStoreService } from '../../../services/revenue/revenue.store.service';
import {
  GetEventRevenueMetricResponseDto,
  GetRevenueKpiStatisticsResponse,
  GetRevenueTrendsResponse,
} from '@smatchy/sdk';

@Component({
  selector: 'bo-event-revenue',
  templateUrl: './event-revenue.component.html',
  styleUrls: ['./event-revenue.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, 
    CommonModule,
    ButtonModule,
    RippleModule,
    InputTextModule,
    DateRangePickerComponent,
    KpiCardComponent,
    RevenueChartComponent,
    TicketMetricsByEventComponent,
  ],
})
export class EventRevenueComponent implements OnInit {
  #revenueStore = inject(RevenueStoreService);
  #translate = inject(TranslateService);
  #destroyRef = inject(DestroyRef);
  #lastKpiResponse: GetRevenueKpiStatisticsResponse | null = null;
  dateRangePicker = viewChild(DateRangePickerComponent);
  dateRange = signal<DateRange>({
    startDate: null,
    endDate: null,
  });
  kpiCards = signal<KpiCardData[]>([]);
  chartData = signal<RevenueChartData>({ labels: [], revenue: [], refunds: [] });
  ticketMetrics = signal<TicketMetricsData[]>([]);
  loading = this.#revenueStore.loading;

  private isInitialized = false;

  private readonly KPI_CONFIG_MAP: Record<
    string,
    {
      title: string;
      icon: string;
      format: 'currency' | 'number' | 'percentage';
    }
  > = {
    'total-event-revenue': {
      title: 'financePages.erKpiTotalEvent',
      icon: 'assets/icons/total-event-revenue.svg',
      format: 'currency',
    },
    'total-smatchy-revenue': {
      title: 'financePages.erKpiTotalSmatchy',
      icon: 'assets/icons/total-smatchy-revenue.svg',
      format: 'currency',
    },
    'total-organizers-revenue': {
      title: 'financePages.erKpiTotalOrganizers',
      icon: 'assets/icons/total-organizers-revenue.svg',
      format: 'currency',
    },
    'tickets-sold': {
      title: 'financePages.erKpiTicketsSold',
      icon: 'assets/icons/tickets-sold.svg',
      format: 'number',
    },
    'refund-rate': {
      title: 'financePages.erKpiRefundRate',
      icon: 'assets/icons/refund-rate.svg',
      format: 'percentage',
    },
    'total-stripe-fees': {
      title: 'financePages.erKpiStripeFees',
      icon: 'assets/icons/total-stripe-fees.svg',
      format: 'currency',
    },
  };

  constructor() {
    effect(
      () => {
        if (this.isInitialized) {
          this.dateRange();
          this.loadData();
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const kpiStats = this.#revenueStore.kpiStats();
        if (kpiStats) {
          this.updateKpiCards(kpiStats);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const trends = this.#revenueStore.trends();
        if (trends) {
          this.updateChartData(trends);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const metrics = this.#revenueStore.eventMetrics();        
        this.updateTicketMetrics(metrics);
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadData();
    this.isInitialized = true;
    this.#translate.onLangChange.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      if (this.#lastKpiResponse) {
        this.updateKpiCards(this.#lastKpiResponse);
      }
    });
  }

  onDateRangeChange(range: DateRange): void {
    this.dateRange.set(range);
  }

  onReset(): void {
    this.dateRangePicker()?.reset();
    this.dateRange.set({
      startDate: null,
      endDate: null,
    });
  }

  getStartDateISO(): string | undefined {
    const startDate = this.dateRange().startDate;
    return startDate ? new Date(startDate).toISOString() : undefined;
  }

  getEndDateISO(): string | undefined {
    const endDate = this.dateRange().endDate;
    return endDate ? new Date(endDate).toISOString() : undefined;
  }

  private loadData(): void {
    const range = this.dateRange();
    const filters = {
      startDate: range.startDate ? new Date(range.startDate).toISOString() : undefined,
      endDate: range.endDate ? new Date(range.endDate).toISOString() : undefined,
    };

    this.#revenueStore.getRevenueKpiStatistics(filters);
    this.#revenueStore.getRevenueTrends(filters);
  }

  private updateKpiCards(response: GetRevenueKpiStatisticsResponse): void {
    this.#lastKpiResponse = response;
    const kpis = response.kpis || [];
    const comparisonSuffix = this.#translate.instant('financePages.erFromLastMonth');

    const kpiCardsData: KpiCardData[] = kpis.map((kpi) => {
      const config = this.KPI_CONFIG_MAP[kpi.type] || {
        title: 'financePages.erKpiUnknown',
        icon: 'assets/icons/default.svg',
        format: 'number' as const,
      };

      return mapApiResponseToKpiCard(
        {
          title: this.#translate.instant(config.title),
          value: kpi.value,
          comparisonPercent: kpi.comparisonPercent,
          showComparison: kpi.showComparison,
          icon: config.icon,
        },
        config.format,
        comparisonSuffix,
      );
    });
    this.kpiCards.set(kpiCardsData);
  }

  private updateChartData(response: GetRevenueTrendsResponse): void {
    const trends = response.trends || [];
    const chartData: RevenueChartData = {
      labels: trends.map((t) => t.period),
      revenue: trends.map((t) => (t.revenue || 0) / 100),
      refunds: trends.map((t) => (t.refunds || 0) / 100),
    };
    this.chartData.set(chartData);
  }

  parseEventDate = (date: string | Date | undefined): Date => {
    if (!date) {
      return new Date();
    }
    return typeof date === 'string' ? new Date(date) : new Date(date);
  };


  private updateTicketMetrics(events: GetEventRevenueMetricResponseDto[]): void {
    const metrics = events.map((event) => ({
      id: event.id ?? '',
      event: event.event?.title || '',
      eventDate: this.parseEventDate(event.event?.startDate),
      locationName: event.event?.location?.name,
      organizerFirstName: event.organizer?.firstName || '',
      organizerLastName: event.organizer?.lastName || '',
      organizerAvatarUrl: event.organizer?.avatarUrl,
      ticketPrice: event.event?.costPerParticipant || 0,
      ticketsProcessed: event.stats?.ticketsProcessed || 0,
      ticketsSold: event.stats?.ticketsSold || 0,
      refunds: event.stats?.totalRefundedAmount || 0,
      refundCount: event.stats?.refundedTickets || 0,
      status: parsePayoutStatus(event.payout?.status),
      statusSubText: getPayoutStatusSubText(event.payout, event?.updatedAt),
      sport: event.event?.sportName || '',
      sportId: event.event?.sportId || '',
      userEmail: event.organizer?.email || '',
      eventRevenue: event.stats?.totalTicketPaid || 0,
      finalEventRevenue: event.stats?.eventRevenue || 0,
      organizerRevenue: event.stats?.organizerEarning || 0,
      smatchyRevenue: event.stats?.platformEarning || 0,
    }));

    this.ticketMetrics.set(metrics);
  }
}
